import React, { useMemo } from 'react';
import { format, isToday } from 'date-fns';
import {
  ArrowPathIcon,
  CalendarIcon,
  MapPinIcon,
  UsersIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { eventsByDay, safeParse } from './calendarDateUtils';
import { colorForEvent, EVENT_COLOR_CLASSES } from './eventColors';
import type { CalendarEvent } from '../../types/integrations';

interface CalendarAgendaViewProps {
  cursorDate: Date;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
}

const formatRange = (event: CalendarEvent): string => {
  if (event.all_day) return 'All day';
  try {
    return `${format(safeParse(event.start), 'h:mma')
      .toLowerCase()
      .replace(/m$/, '')} – ${format(safeParse(event.end), 'h:mma')
      .toLowerCase()
      .replace(/m$/, '')}`;
  } catch {
    return '';
  }
};

const CalendarAgendaView: React.FC<CalendarAgendaViewProps> = ({
  events,
  onEventClick,
}) => {
  const eventMap = useMemo(() => eventsByDay(events), [events]);

  // Sort day keys chronologically
  const sortedKeys = useMemo(
    () => Array.from(eventMap.keys()).sort(),
    [eventMap]
  );

  if (sortedKeys.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <CalendarIcon className="w-10 h-10 text-text-tertiary mb-3" />
        <p className="text-sm font-medium text-text-primary">No events in this window</p>
        <p className="text-xs text-text-secondary mt-1">
          Course deadlines and sessions appear here automatically.
        </p>
        <p className="text-xs text-text-tertiary mt-1">
          Events you create in Tereiko will show up here.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 py-4 space-y-6 max-h-[700px] overflow-y-auto">
      {sortedKeys.map((key) => {
        const list = eventMap.get(key) ?? [];
        const date = new Date(`${key}T12:00:00`);
        const today = isToday(date);
        return (
          <section key={key}>
            <header className="flex items-baseline gap-3 mb-2 sticky top-0 bg-surface-elevated/95 backdrop-blur-sm py-2">
              <span
                className={[
                  'inline-flex items-center justify-center min-w-[28px] h-7 rounded-full text-xs font-bold',
                  today ? 'bg-error-500 text-white' : 'bg-surface-primary text-text-primary',
                ].join(' ')}
              >
                {format(date, 'd')}
              </span>
              <span className="text-sm font-semibold text-text-primary">
                {format(date, 'EEEE')}
              </span>
              <span className="text-xs text-text-tertiary">{format(date, 'MMMM yyyy')}</span>
            </header>
            <ul className="flex flex-col gap-2">
              {list.map((event) => {
                const c = EVENT_COLOR_CLASSES[colorForEvent(event.id)];
                return (
                  <li key={event.id}>
                    <button
                      type="button"
                      onClick={() => onEventClick?.(event)}
                      className="w-full flex items-start gap-3 p-3 rounded-2 border border-border-primary/15 bg-surface-primary hover:bg-action-hover active:scale-[0.99] transition-all text-left"
                    >
                      <span className={['w-2.5 h-2.5 rounded-full mt-1.5 shrink-0', c.dot].join(' ')} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-text-secondary">
                            {formatRange(event)}
                          </span>
                          {event.is_tereiko && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-primary-500 px-1.5 py-0.5 bg-primary-500/12 rounded-full">
                              Tereiko
                            </span>
                          )}
                          {(() => {
                            const self = event.attendees?.find((a) => a.self);
                            if (self?.response_status === 'needsAction') {
                              return (
                                <span className="text-[10px] font-bold uppercase tracking-wider text-warning-700 dark:text-warning-200 px-1.5 py-0.5 bg-warning-500/16 rounded-full">
                                  Needs response
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <p className="text-sm font-semibold text-text-primary truncate mt-0.5">
                          {event.summary || 'Untitled event'}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-text-tertiary">
                          {event.is_recurring && (
                            <span className="inline-flex items-center gap-1">
                              <ArrowPathIcon className="w-3.5 h-3.5" />
                              Recurring
                            </span>
                          )}
                          {event.meeting_link && (
                            <span className="inline-flex items-center gap-1">
                              <VideoCameraIcon className="w-3.5 h-3.5" />
                              Meet
                            </span>
                          )}
                          {event.location && (
                            <span className="inline-flex items-center gap-1 truncate">
                              <MapPinIcon className="w-3.5 h-3.5" />
                              {event.location}
                            </span>
                          )}
                          {(event.attendees_count ?? event.attendees?.length ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <UsersIcon className="w-3.5 h-3.5" />
                              {event.attendees_count ?? event.attendees!.length} attendee
                              {(event.attendees_count ?? event.attendees!.length) === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

export default CalendarAgendaView;
